import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { FaPhone } from 'react-icons/fa';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  TechBackground, 
  CircuitLines, 
  TechParticles, 
  HolographicScanner,
  BorderGlow,
  TechTitle,
  TechSubmitButton,
  TechInput,
  TechLabel,
  TechFormGroup,
  TechFormContainer
} from '../../styles/TechStylesModal';

// Container principal - altura baseada na duração do vídeo
const PageContainer = styled.div`
  width: 100%;
  min-height: ${props => props.$scrollHeight}px;
  background: #000;
  position: relative;
`;

// Container fixo do vídeo que ocupa toda a viewport
const VideoContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  z-index: 1;
`;

// Vídeo em tela cheia
const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// Indicador de progresso opcional (pode ser removido depois)
const ProgressIndicator = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 8px 16px;
  border-radius: 20px;
  color: white;
  font-size: 12px;
  z-index: 10;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s ease;
`;

// Barra de navegação lateral
const ScrollNavBar = styled.div`
  position: fixed;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.5s ease;
  pointer-events: ${props => props.$visible ? 'auto' : 'none'};
  padding: 12px 8px;
  background: rgba(0, 0, 0, 0.4);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(100, 182, 255, 0.2);
  box-shadow: 0 0 20px rgba(100, 182, 255, 0.15);

  @media (max-width: 768px) {
    right: 14px;
    padding: 10px 6px;
  }
`;

// Trilha de progresso da barra
const ScrollTrack = styled.div`
  position: relative;
  width: 4px;
  height: 220px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: visible;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    height: 180px;
  }
`;

// Preenchimento de progresso
const ScrollProgress = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(180deg, #64B6FF 0%, #00FFFF 100%);
  border-radius: 4px;
  height: ${props => props.$progress}%;
  transition: height 0.15s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 0 15px rgba(100, 182, 255, 0.6), 0 0 30px rgba(0, 255, 255, 0.3);
`;

// Container dos marcadores
const MarkersContainer = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 0;
`;

// Marcador individual
const Marker = styled.button`
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid ${props => props.$active ? '#00FFFF' : 'rgba(255, 255, 255, 0.5)'};
  background: ${props => props.$active ? 'rgba(0, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.6)'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  left: 50%;
  transform: translateX(-50%) scale(${props => props.$active ? 1.2 : 1});
  top: ${props => props.$position}%;
  z-index: 2;
  box-shadow: ${props => props.$active ? '0 0 12px rgba(0, 255, 255, 0.6)' : '0 0 6px rgba(100, 182, 255, 0.3)'};

  &:hover {
    border-color: #64B6FF;
    background: rgba(100, 182, 255, 0.4);
    transform: translateX(-50%) scale(1.4);
    box-shadow: 0 0 15px rgba(100, 182, 255, 0.5);
  }

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${props => props.$active ? '6px' : '0'};
    height: ${props => props.$active ? '6px' : '0'};
    background: #00FFFF;
    border-radius: 50%;
    transition: all 0.3s ease;
    box-shadow: ${props => props.$active ? '0 0 8px #00FFFF' : 'none'};
  }

  @media (max-width: 768px) {
    width: 14px;
    height: 14px;
  }
`;

// Tooltip do marcador
const MarkerTooltip = styled.span`
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
  backdrop-filter: blur(5px);
  border: 1px solid rgba(100, 182, 255, 0.3);

  ${Marker}:hover & {
    opacity: 1;
    transform: translateY(-50%) translateX(-5px);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Indicador de scroll
const ScrollHint = styled.div`
  position: fixed;
  bottom: 50px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  z-index: 10;
  animation: fadeFloat 3s ease-in-out infinite;
  
  &::before {
    content: 'Role para continuar';
    color: rgba(255, 255, 255, 0.95);
    font-size: 13px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 4px;
    font-family: 'Inter', sans-serif;
    text-shadow: 0 0 10px rgba(100, 182, 255, 0.6), 0 0 20px rgba(100, 182, 255, 0.4);
  }
  
  &::after {
    content: '';
    width: 1px;
    height: 40px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.6), transparent);
    animation: lineGrow 2s ease-in-out infinite;
  }
  
  @keyframes fadeFloat {
    0%, 100% {
      opacity: 0.8;
      transform: translateX(-50%) translateY(0);
    }
    50% {
      opacity: 1;
      transform: translateX(-50%) translateY(-5px);
    }
  }
  
  @keyframes lineGrow {
    0%, 100% {
      height: 40px;
      opacity: 0.6;
    }
    50% {
      height: 50px;
      opacity: 1;
    }
  }
`;

// Animação de pulso para o botão
const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(100, 182, 255, 0.7);
  }
  70% {
    box-shadow: 0 0 0 20px rgba(100, 182, 255, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(100, 182, 255, 0);
  }
`;

// Animação de fade in
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

// Container do botão de chamada Vapi
const VapiButtonContainer = styled.div`
  position: fixed;
  bottom: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  animation: ${fadeInUp} 0.6s ease-out forwards;
`;

// Botão de chamada Vapi
const VapiCallButton = styled.button`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #64B6FF 0%, #0096FF 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 32px;
  transition: all 0.3s ease;
  animation: ${pulseAnimation} 2s infinite;
  
  &:hover {
    transform: scale(1.1);
    background: linear-gradient(135deg, #7ec4ff 0%, #2aa6ff 100%);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  }
`;

// Texto abaixo do botão
const VapiButtonText = styled.span`
  color: white;
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 2px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  background: linear-gradient(90deg, #64B6FF, #00FFFF);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

// Animações para o modal
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const modalFadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const modalFadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-20px); }
`;

const successPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 255, 200, 0.7);
    background-color: rgba(0, 255, 200, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(0, 255, 200, 0);
    background-color: rgba(0, 255, 200, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 255, 200, 0);
    background-color: rgba(0, 255, 200, 0.4);
  }
`;

const errorPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 50, 50, 0.7);
    background-color: rgba(255, 50, 50, 0.4);
  }
  50% {
    box-shadow: 0 0 0 20px rgba(255, 50, 50, 0);
    background-color: rgba(255, 50, 50, 0.7);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 50, 50, 0);
    background-color: rgba(255, 50, 50, 0.4);
  }
`;

const dataFlowAnimation = keyframes`
  0% {
    background-position: 0% 0%;
    opacity: 0.3;
  }
  50% {
    opacity: 0.8;
  }
  100% {
    background-position: 100% 100%;
    opacity: 0.3;
  }
`;

const iconRotate = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.2);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
`;

const textReveal = keyframes`
  0% {
    clip-path: polygon(0 0, 0 0, 0 100%, 0 100%);
    opacity: 0;
  }
  100% {
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%);
    opacity: 1;
  }
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulseGlow = keyframes`
  0% { box-shadow: 0 0 5px rgba(0, 255, 200, 0.5); }
  50% { box-shadow: 0 0 15px rgba(0, 255, 200, 0.8); }
  100% { box-shadow: 0 0 5px rgba(0, 255, 200, 0.5); }
`;

// Estilos para o modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: ${props => props.isClosing ? fadeOut : fadeIn} 0.3s ease-in-out;
`;

const ModalContent = styled.div`
  position: relative;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 0 30px rgba(0, 200, 255, 0.2);
  animation: ${props => props.isClosing ? modalFadeOut : modalFadeIn} 0.3s ease-in-out;
`;

const ModalBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(10, 15, 30, 0.9);
  border: 1px solid rgba(0, 200, 255, 0.3);
  z-index: 0;
`;

const ModalBody = styled.div`
  position: relative;
  z-index: 1;
  padding: 30px;
  width: 100%;
  height: 100%;
  max-height: 80vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalTitle = styled(TechTitle)`
  margin-bottom: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  color: rgba(0, 200, 255, 0.8);
  font-size: 24px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &::before, &::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 2px;
    background-color: rgba(0, 200, 255, 0.8);
    transition: all 0.3s ease;
  }
  
  &::before {
    transform: rotate(45deg);
  }
  
  &::after {
    transform: rotate(-45deg);
  }
  
  &:hover {
    transform: scale(1.1);
    
    &::before, &::after {
      background-color: rgba(0, 255, 255, 1);
      box-shadow: 0 0 8px rgba(0, 255, 255, 0.8);
    }
  }
`;

const Form = styled(TechFormContainer)`
  margin-top: 20px;
`;

const FormGroup = styled(TechFormGroup)``;

const Label = styled(TechLabel)``;

const Input = styled(TechInput)``;

const SubmitButton = styled(TechSubmitButton)``;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  margin-left: 10px;
  border: 2px solid rgba(0, 255, 200, 0.3);
  border-top: 2px solid rgba(0, 255, 200, 1);
  border-radius: 50%;
  animation: ${spinAnimation} 1s linear infinite;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: 50%;
    border: 1px solid rgba(0, 255, 200, 0.5);
    animation: ${pulseGlow} 1.5s ease-in-out infinite;
  }
`;

const StatusMessage = styled.div`
  margin-top: 15px;
  padding: 15px;
  border-radius: 8px;
  text-align: center;
  font-family: 'Orbitron', sans-serif;
  font-size: 16px;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100px;
  background-color: ${props => props.success ? 'rgba(0, 40, 40, 0.6)' : 'rgba(40, 0, 0, 0.6)'};
  color: ${props => props.success ? 'rgba(0, 255, 200, 1)' : 'rgba(255, 50, 50, 1)'};
  border: 1px solid ${props => props.success ? 'rgba(0, 255, 200, 0.5)' : 'rgba(255, 50, 50, 0.5)'};
  box-shadow: 0 0 15px ${props => props.success ? 'rgba(0, 255, 200, 0.3)' : 'rgba(255, 50, 50, 0.3)'};
  animation: ${fadeIn} 0.3s ease-in-out;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: ${props => props.success ? 
      `linear-gradient(45deg, rgba(0, 255, 200, 0.1) 25%, transparent 25%, transparent 50%, rgba(0, 255, 200, 0.1) 50%, rgba(0, 255, 200, 0.1) 75%, transparent 75%, transparent)` : 
      `linear-gradient(45deg, rgba(255, 50, 50, 0.1) 25%, transparent 25%, transparent 50%, rgba(255, 50, 50, 0.1) 50%, rgba(255, 50, 50, 0.1) 75%, transparent 75%, transparent)`
    };
    background-size: 20px 20px;
    opacity: 0.5;
    z-index: 0;
    animation: ${dataFlowAnimation} 3s infinite linear;
  }
`;

const StatusIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  position: relative;
  z-index: 1;
  background-color: ${props => props.success ? 'rgba(0, 255, 200, 0.2)' : 'rgba(255, 50, 50, 0.2)'};
  color: ${props => props.success ? 'rgba(0, 255, 200, 1)' : 'rgba(255, 50, 50, 1)'};
  font-size: 20px;
  animation: ${props => props.success ? successPulse : errorPulse} 2s infinite;
  
  &::before {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    border: 1px solid ${props => props.success ? 'rgba(0, 255, 200, 0.5)' : 'rgba(255, 50, 50, 0.5)'};
    animation: ${iconRotate} 4s infinite linear;
  }
`;

const StatusText = styled.div`
  position: relative;
  z-index: 1;
  font-weight: 500;
  letter-spacing: 1px;
  animation: ${textReveal} 0.5s forwards;
`;

// Lista de vídeos em sequência
const VIDEOS = [
  '/bio/transicao_1.mp4',
  '/bio/transicao_2.mp4',
  '/bio/transicao_3.mp4'
];

const BioPage = () => {
  const video1Ref = useRef(null);
  const video2Ref = useRef(null);
  const video3Ref = useRef(null);
  const introVideoRef = useRef(null);
  const [videoDurations, setVideoDurations] = useState(VIDEOS.map(() => 0));
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasStartedScrolling, setHasStartedScrolling] = useState(false);
  const [introVideoLoaded, setIntroVideoLoaded] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videosLoadedCount, setVideosLoadedCount] = useState(0);
  const [showFormButton, setShowFormButton] = useState(false);
  const [isDraggingTrack, setIsDraggingTrack] = useState(false);
  
  // Estados do modal e formulário
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    empresa: '',
    segmento: 'imobiliário'
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ message: '', success: false, show: false });
  const navigate = useNavigate();
  // Estado para rastrear quais vídeos estão realmente prontos para exibição
  const [videosReady, setVideosReady] = useState(VIDEOS.map(() => false));
  // Vídeo anterior visível como fallback durante transição
  const [previousVideoIndex, setPreviousVideoIndex] = useState(0);
  
  // Refs para controle de reprodução
  const scrollTrackRef = useRef(null);
  const targetTimeRef = useRef(0);
  const animationFrameRef = useRef(null);
  const isPlayingRef = useRef(VIDEOS.map(() => false));
  const currentVideoIndexRef = useRef(0);
  const isInitializedRef = useRef(false);
  const lastVisibleVideoRef = useRef(0);
  
  // Refs para controle de snap nas transições
  const lastScrollYRef = useRef(0);
  const isSnappingRef = useRef(false);
  const snapTimeoutRef = useRef(null);
  const scrollDirectionRef = useRef(null); // 'down' ou 'up'
  const allowedTransitionRef = useRef(0); // Índice da transição permitida atual
  const lastTransitionIndexRef = useRef(0); // Última transição conhecida
  
  // Força o scroll para o topo e desabilita scroll restoration do navegador
  useEffect(() => {
    // Desabilita a restauração automática de scroll do navegador
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    
    // Força o scroll para o topo imediatamente
    window.scrollTo(0, 0);
    
    // Marca como inicializado após garantir que o scroll foi resetado
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      isInitializedRef.current = true;
    }, 50);
    
    return () => {
      clearTimeout(timeoutId);
      // Restaura o comportamento padrão ao sair da página
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, []);

  // Multiplicador para a altura do scroll (menor = menos scroll necessário)
  const scrollMultiplier = 0.25;
  
  // Duração total de todos os vídeos
  const totalDuration = videoDurations.reduce((sum, d) => sum + d, 0);
  
  // Altura total do scroll baseada na duração total
  const scrollHeight = totalDuration * scrollMultiplier * window.innerHeight;

  // Retorna a ref do vídeo pelo índice
  const getVideoRef = (index) => {
    if (index === 0) return video1Ref;
    if (index === 1) return video2Ref;
    return video3Ref;
  };

  // Quando cada vídeo carrega, captura a duração
  const handleLoadedMetadata = (index) => {
    const videoRef = getVideoRef(index);
    const video = videoRef.current;
    if (video) {
      setVideoDurations(prev => {
        const newDurations = [...prev];
        newDurations[index] = video.duration;
        return newDurations;
      });
      video.pause();
      setVideosLoadedCount(prev => prev + 1);
    }
  };

  // Handler quando o vídeo tem dados suficientes para exibir (canplaythrough)
  const handleCanPlayThrough = useCallback((index) => {
    setVideosReady(prev => {
      const newReady = [...prev];
      newReady[index] = true;
      return newReady;
    });
  }, []);

  // Força pré-carregamento agressivo dos vídeos em iOS
  useEffect(() => {
    const preloadVideos = async () => {
      const videoRefs = [video1Ref, video2Ref, video3Ref];
      
      for (let i = 0; i < videoRefs.length; i++) {
        const video = videoRefs[i].current;
        if (video) {
          // Força carregamento tentando reproduzir e pausando imediatamente
          try {
            video.muted = true;
            video.currentTime = 0;
            await video.play();
            video.pause();
            video.currentTime = 0;
          } catch (e) {
            // Ignora erros - apenas tentativa de pré-carregamento
          }
        }
      }
    };

    // Executa após um pequeno delay para garantir que os refs estão prontos
    const timeoutId = setTimeout(preloadVideos, 500);
    return () => clearTimeout(timeoutId);
  }, []);

  // Marca como carregado quando todos os vídeos estiverem prontos
  useEffect(() => {
    if (videosLoadedCount === VIDEOS.length && totalDuration > 0) {
      setIsLoaded(true);
    }
  }, [videosLoadedCount, totalDuration]);

  // Calcula qual vídeo e tempo baseado no tempo global
  const getVideoAndTime = useCallback((globalTime) => {
    let accumulated = 0;
    for (let i = 0; i < videoDurations.length; i++) {
      const duration = videoDurations[i];
      if (globalTime <= accumulated + duration) {
        return { videoIndex: i, localTime: globalTime - accumulated };
      }
      accumulated += duration;
    }
    // Se passou de tudo, retorna o último vídeo no final
    const lastIndex = videoDurations.length - 1;
    return { videoIndex: lastIndex, localTime: videoDurations[lastIndex] };
  }, [videoDurations]);

  // Loop de animação que reproduz o vídeo até o tempo alvo
  const animate = useCallback(() => {
    if (!isLoaded || totalDuration === 0) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    const globalTargetTime = targetTimeRef.current;
    const { videoIndex, localTime: targetLocalTime } = getVideoAndTime(globalTargetTime);
    
    // Atualiza o índice do vídeo atual se mudou
    if (videoIndex !== currentVideoIndexRef.current) {
      // Guarda o índice anterior como fallback antes de mudar
      const prevIndex = currentVideoIndexRef.current;
      
      // Verifica se o próximo vídeo está pronto - se não, mantém o anterior visível
      const nextVideoRef = getVideoRef(videoIndex);
      const nextVideo = nextVideoRef.current;
      const isNextReady = nextVideo && nextVideo.readyState >= 3; // HAVE_FUTURE_DATA ou melhor
      
      // Só atualiza o previousVideoIndex se o próximo está pronto
      if (isNextReady) {
        setPreviousVideoIndex(prevIndex);
        lastVisibleVideoRef.current = prevIndex;
      }
      
      // Pausa o vídeo anterior
      const prevVideoRef = getVideoRef(prevIndex);
      if (prevVideoRef.current) {
        prevVideoRef.current.pause();
        isPlayingRef.current[prevIndex] = false;
      }
      currentVideoIndexRef.current = videoIndex;
      setCurrentVideoIndex(videoIndex);
    }

    const videoRef = getVideoRef(videoIndex);
    const video = videoRef.current;
    if (!video) {
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    const currentTime = video.currentTime;
    const diff = targetLocalTime - currentTime;
    const absDiff = Math.abs(diff);

    // Se chegou perto o suficiente do alvo, para
    if (absDiff < 0.03) {
      if (isPlayingRef.current[videoIndex]) {
        video.pause();
        isPlayingRef.current[videoIndex] = false;
      }
      animationFrameRef.current = requestAnimationFrame(animate);
      return;
    }

    // Calcula a velocidade de reprodução baseada na distância
    const baseSpeed = Math.min(Math.max(absDiff * 3, 0.5), 8);
    
    if (diff > 0) {
      // Precisa avançar - usa playbackRate positivo
      video.playbackRate = baseSpeed;
      if (!isPlayingRef.current[videoIndex] || video.paused) {
        video.play().catch(() => {});
        isPlayingRef.current[videoIndex] = true;
      }
    } else {
      // Precisa voltar
      video.pause();
      isPlayingRef.current[videoIndex] = false;
      const step = Math.min(absDiff, 0.05);
      video.currentTime = Math.max(0, currentTime - step);
    }

    animationFrameRef.current = requestAnimationFrame(animate);
  }, [isLoaded, totalDuration, getVideoAndTime]);

  // Inicia o loop de animação
  useEffect(() => {
    if (isLoaded) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isLoaded, animate]);

  // Detecta quando o usuário começa a rolar
  useEffect(() => {
    const handleFirstScroll = () => {
      // Só detecta scroll após a inicialização estar completa
      if (!isInitializedRef.current) return;
      
      if (window.scrollY > 10 && !hasStartedScrolling) {
        setHasStartedScrolling(true);
        // Pausa o vídeo de intro
        if (introVideoRef.current) {
          introVideoRef.current.pause();
        }
      }
    };

    window.addEventListener('scroll', handleFirstScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleFirstScroll);
  }, [hasStartedScrolling]);

  // Calcula os limites de tempo para cada transição
  const getTransitionBoundaries = useCallback(() => {
    const boundaries = [];
    let accumulated = 0;
    
    for (let i = 0; i < videoDurations.length; i++) {
      boundaries.push({
        start: accumulated,
        end: accumulated + videoDurations[i],
        index: i
      });
      accumulated += videoDurations[i];
    }
    
    return boundaries;
  }, [videoDurations]);

  // Função para fazer snap suave para uma posição de tempo (duração mais lenta como o clique)
  const snapToTime = useCallback((targetTime, newAllowedTransition = null, duration = 2500) => {
    if (!isLoaded || totalDuration === 0 || scrollHeight === 0) return;
    
    isSnappingRef.current = true;
    
    // Atualiza a transição permitida se especificado
    if (newAllowedTransition !== null) {
      allowedTransitionRef.current = newAllowedTransition;
    }
    
    const maxScroll = scrollHeight - window.innerHeight;
    const scrollPercent = Math.min(targetTime / totalDuration, 1);
    const targetScroll = scrollPercent * maxScroll;
    
    const startPosition = window.scrollY;
    const distance = targetScroll - startPosition;
    const startTime = performance.now();
    
    // Easing mais suave para animação lenta
    const easeInOutCubic = (t) => {
      return t < 0.5 
        ? 4 * t * t * t 
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };
    
    const animateSnap = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      
      const newScrollPos = startPosition + distance * easedProgress;
      window.scrollTo(0, newScrollPos);
      
      // Atualiza o progresso visual durante o snap
      const newScrollPercent = Math.min(Math.max(newScrollPos / maxScroll, 0), 1);
      setScrollProgress(newScrollPercent);
      targetTimeRef.current = newScrollPercent * totalDuration;
      
      if (progress < 1) {
        requestAnimationFrame(animateSnap);
      } else {
        // Finaliza o snap e atualiza a referência da última transição
        lastTransitionIndexRef.current = allowedTransitionRef.current;
        lastScrollYRef.current = window.scrollY;
        setTimeout(() => {
          isSnappingRef.current = false;
        }, 50);
      }
    };
    
    requestAnimationFrame(animateSnap);
  }, [isLoaded, totalDuration, scrollHeight]);

  // Efeito para atualizar o tempo alvo baseado no scroll com snap nas transições
  // IMPORTANTE: Qualquer scroll dispara IMEDIATAMENTE o snap para fim/início da transição atual
  useEffect(() => {
    if (!isLoaded || totalDuration === 0 || !hasStartedScrolling) return;

    const boundaries = getTransitionBoundaries();
    if (boundaries.length === 0) return;

    const handleScroll = () => {
      // Se está no meio de um snap, BLOQUEIA completamente qualquer processamento
      if (isSnappingRef.current) {
        return;
      }
      
      const scrollTop = window.scrollY;
      
      // Detecta a direção do scroll
      const scrollDelta = scrollTop - lastScrollYRef.current;
      
      // Só processa se houve movimento significativo
      if (Math.abs(scrollDelta) < 5) {
        return;
      }
      
      const direction = scrollDelta > 0 ? 'down' : 'up';
      
      // Obtém os limites da transição atual permitida
      const currentAllowed = allowedTransitionRef.current;
      const currentBoundary = boundaries[currentAllowed];
      
      if (!currentBoundary) {
        lastScrollYRef.current = scrollTop;
        return;
      }
      
      // Calcula o progresso atual dentro da transição
      const transitionDuration = currentBoundary.end - currentBoundary.start;
      const currentTime = targetTimeRef.current;
      const timeInTransition = Math.max(0, Math.min(currentTime - currentBoundary.start, transitionDuration));
      const progressInTransition = timeInTransition / transitionDuration;
      
      // IMEDIATAMENTE dispara o snap baseado na direção
      // Não importa o quanto o usuário scrollou - vai para o fim/início da transição ATUAL
      
      if (direction === 'down') {
        // Verifica se já está no final (97%+) - pode avançar para próxima
        const isAtEnd = progressInTransition >= 0.97;
        const canAdvance = currentAllowed < boundaries.length - 1;
        
        if (isAtEnd && canAdvance) {
          // Já terminou a transição atual, avança para a próxima
          const nextTransition = currentAllowed + 1;
          snapToTime(boundaries[nextTransition].end, nextTransition, 2500);
        } else {
          // Vai para o fim da transição ATUAL (não pula)
          snapToTime(currentBoundary.end, currentAllowed, 2500);
        }
      } else if (direction === 'up') {
        // Verifica se já está no início (3%-) - pode voltar para anterior
        const isAtStart = progressInTransition <= 0.03;
        const canGoBack = currentAllowed > 0;
        
        if (isAtStart && canGoBack) {
          // Já está no início da transição atual, volta para a anterior
          const prevTransition = currentAllowed - 1;
          snapToTime(boundaries[prevTransition].start, prevTransition, 2500);
        } else {
          // Vai para o início da transição ATUAL (não pula)
          snapToTime(currentBoundary.start, currentAllowed, 2500);
        }
      }
      
      lastScrollYRef.current = scrollTop;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Inicializa a referência
    lastScrollYRef.current = window.scrollY;

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (snapTimeoutRef.current) {
        clearTimeout(snapTimeoutRef.current);
      }
    };
  }, [isLoaded, totalDuration, scrollHeight, hasStartedScrolling, getTransitionBoundaries, snapToTime]);

  // Detecta quando o usuário chega ao final do transicao_3 (95% ou mais)
  useEffect(() => {
    if (scrollProgress >= 0.95 && !showFormButton) {
      setShowFormButton(true);
    } else if (scrollProgress < 0.90 && showFormButton) {
      setShowFormButton(false);
    }
  }, [scrollProgress, showFormButton]);

  // Função para formatar o número de telefone
  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers.length ? `(${numbers}` : '';
    } else if (numbers.length <= 7) {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2)}`;
    } else {
      return `(${numbers.substring(0, 2)}) ${numbers.substring(2, 7)}-${numbers.substring(7, 11)}`;
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setIsModalClosing(false);
  };

  const handleCloseModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalClosing(false);
      setFormData({
        nome: '',
        telefone: '',
        empresa: '',
        segmento: 'imobiliário'
      });
      setStatus({ message: '', success: false, show: false });
    }, 300);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCloseModal();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'telefone') {
      setFormData(prev => ({
        ...prev,
        [name]: formatPhoneNumber(value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ message: '', success: false, show: false });

    try {
      const response = await axios.post(
        'https://webhook-editor.abianca.com.br/webhook/7c0f87b7-caa3-4378-aa98-043d3befb747',
        formData
      );
      
      if (response.data && response.data.includes('sucesso')) {
        console.log('Dados enviados com sucesso para o webhook!');
        navigate('/ty');
      } else {
        console.error('O webhook retornou fracasso:', response.data);
        setStatus({
          message: 'Houve um erro ao processar sua solicitação. Tente novamente.',
          success: false,
          show: true
        });
        setLoading(false);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus({
        message: 'Houve um erro ao processar sua solicitação. Tente novamente.',
        success: false,
        show: true
      });
      setLoading(false);
    }
  };

  // Calcula a posição percentual de cada marcador baseado na duração dos vídeos
  const getMarkerPositions = useCallback(() => {
    if (totalDuration === 0) return [0, 33, 66, 100];
    
    let accumulated = 0;
    const positions = [];
    
    for (let i = 0; i < videoDurations.length; i++) {
      const position = (accumulated / totalDuration) * 100;
      positions.push(position);
      accumulated += videoDurations[i];
    }
    
    // Adiciona marcador no final (100%)
    positions.push(100);
    
    return positions;
  }, [videoDurations, totalDuration]);

  // Função de scroll suave customizada (mais lenta e fluida)
  const smoothScrollTo = useCallback((targetPosition, duration = 1500) => {
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    const startTime = performance.now();
    
    const easeInOutCubic = (t) => {
      return t < 0.5 
        ? 4 * t * t * t 
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };
    
    const animateScroll = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      
      window.scrollTo(0, startPosition + distance * easedProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };
    
    requestAnimationFrame(animateScroll);
  }, []);

  // Handler para navegação ao clicar no marcador
  const handleMarkerClick = useCallback((index) => {
    if (!isLoaded || totalDuration === 0 || scrollHeight === 0) return;
    if (isSnappingRef.current) return;
    
    const boundaries = getTransitionBoundaries();
    
    // Inicia o scroll se ainda não começou
    if (!hasStartedScrolling) {
      setHasStartedScrolling(true);
      if (introVideoRef.current) {
        introVideoRef.current.pause();
      }
    }
    
    // Se é o último marcador (final), vai para o final da última transição
    if (index >= videoDurations.length) {
      const lastIndex = videoDurations.length - 1;
      snapToTime(boundaries[lastIndex].end, lastIndex, 2500);
    } else {
      // Vai para o início da transição selecionada
      snapToTime(boundaries[index].start, index, 2500);
    }
  }, [isLoaded, totalDuration, scrollHeight, videoDurations, hasStartedScrolling, getTransitionBoundaries, snapToTime]);

  const markerPositions = getMarkerPositions();

  // Handler para calcular a posição do scroll baseada na posição do arrasto na barra
  // Agora também atualiza a transição permitida baseada na posição
  const calculateScrollFromTrackPosition = useCallback((clientY) => {
    if (!scrollTrackRef.current || !isLoaded || scrollHeight === 0 || totalDuration === 0) return;
    
    const trackRect = scrollTrackRef.current.getBoundingClientRect();
    const trackHeight = trackRect.height;
    const relativeY = clientY - trackRect.top;
    
    // Calcula a porcentagem (0 a 1) baseada na posição Y relativa
    const percentage = Math.min(Math.max(relativeY / trackHeight, 0), 1);
    
    // Calcula o tempo global baseado na porcentagem
    const targetGlobalTime = percentage * totalDuration;
    
    // Encontra qual transição corresponde a este tempo
    const boundaries = getTransitionBoundaries();
    let targetTransitionIndex = 0;
    
    for (let i = 0; i < boundaries.length; i++) {
      if (targetGlobalTime >= boundaries[i].start && targetGlobalTime <= boundaries[i].end) {
        targetTransitionIndex = i;
        break;
      }
      if (targetGlobalTime > boundaries[i].end) {
        targetTransitionIndex = i;
      }
    }
    
    // Atualiza a transição permitida
    allowedTransitionRef.current = targetTransitionIndex;
    
    // Converte para posição de scroll
    const maxScroll = scrollHeight - window.innerHeight;
    const targetScroll = percentage * maxScroll;
    
    // Aplica o scroll diretamente
    window.scrollTo(0, targetScroll);
    
    // Atualiza o progresso
    setScrollProgress(percentage);
    targetTimeRef.current = targetGlobalTime;
    lastScrollYRef.current = targetScroll;
  }, [isLoaded, scrollHeight, totalDuration, getTransitionBoundaries]);

  // Handlers para arrastar na barra (mouse)
  const handleTrackMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Inicia o scroll se ainda não começou
    if (!hasStartedScrolling) {
      setHasStartedScrolling(true);
      if (introVideoRef.current) {
        introVideoRef.current.pause();
      }
    }
    
    setIsDraggingTrack(true);
    calculateScrollFromTrackPosition(e.clientY);
  }, [hasStartedScrolling, calculateScrollFromTrackPosition]);

  const handleTrackMouseMove = useCallback((e) => {
    if (!isDraggingTrack) return;
    e.preventDefault();
    calculateScrollFromTrackPosition(e.clientY);
  }, [isDraggingTrack, calculateScrollFromTrackPosition]);

  const handleTrackMouseUp = useCallback(() => {
    setIsDraggingTrack(false);
    
    // Após soltar, faz snap para o final da transição atual
    const boundaries = getTransitionBoundaries();
    const currentAllowed = allowedTransitionRef.current;
    if (boundaries[currentAllowed]) {
      snapToTime(boundaries[currentAllowed].end, currentAllowed, 2500);
    }
  }, [getTransitionBoundaries, snapToTime]);

  // Handlers para arrastar na barra (touch)
  const handleTrackTouchStart = useCallback((e) => {
    e.stopPropagation();
    
    // Inicia o scroll se ainda não começou
    if (!hasStartedScrolling) {
      setHasStartedScrolling(true);
      if (introVideoRef.current) {
        introVideoRef.current.pause();
      }
    }
    
    setIsDraggingTrack(true);
    if (e.touches.length > 0) {
      calculateScrollFromTrackPosition(e.touches[0].clientY);
    }
  }, [hasStartedScrolling, calculateScrollFromTrackPosition]);

  const handleTrackTouchMove = useCallback((e) => {
    if (!isDraggingTrack) return;
    e.preventDefault();
    if (e.touches.length > 0) {
      calculateScrollFromTrackPosition(e.touches[0].clientY);
    }
  }, [isDraggingTrack, calculateScrollFromTrackPosition]);

  const handleTrackTouchEnd = useCallback(() => {
    setIsDraggingTrack(false);
    
    // Após soltar, faz snap para o final da transição atual
    const boundaries = getTransitionBoundaries();
    const currentAllowed = allowedTransitionRef.current;
    if (boundaries[currentAllowed]) {
      snapToTime(boundaries[currentAllowed].end, currentAllowed, 2500);
    }
  }, [getTransitionBoundaries, snapToTime]);

  // Efeito para adicionar listeners globais de mouse/touch quando estiver arrastando
  useEffect(() => {
    if (isDraggingTrack) {
      window.addEventListener('mousemove', handleTrackMouseMove);
      window.addEventListener('mouseup', handleTrackMouseUp);
      window.addEventListener('touchmove', handleTrackTouchMove, { passive: false });
      window.addEventListener('touchend', handleTrackTouchEnd);
      
      return () => {
        window.removeEventListener('mousemove', handleTrackMouseMove);
        window.removeEventListener('mouseup', handleTrackMouseUp);
        window.removeEventListener('touchmove', handleTrackTouchMove);
        window.removeEventListener('touchend', handleTrackTouchEnd);
      };
    }
  }, [isDraggingTrack, handleTrackMouseMove, handleTrackMouseUp, handleTrackTouchMove, handleTrackTouchEnd]);

  // Função para ir até o FINAL de uma transição específica (reproduz o vídeo)
  const scrollToEndOfTransition = useCallback((transitionIndex) => {
    if (!isLoaded || totalDuration === 0 || scrollHeight === 0) return;
    
    const boundaries = getTransitionBoundaries();
    if (!boundaries[transitionIndex]) return;
    
    // Atualiza a transição permitida e faz snap para o final dela
    const targetTime = boundaries[transitionIndex].end;
    
    // Usa o snapToTime com a duração mais lenta (2500ms)
    snapToTime(targetTime, transitionIndex, 2500);
  }, [isLoaded, totalDuration, scrollHeight, getTransitionBoundaries, snapToTime]);

  // Handler para clique na tela - avança para próxima transição
  const handleScreenClick = useCallback((e) => {
    // Ignora cliques em botões ou elementos interativos
    if (e.target.tagName === 'BUTTON' || e.target.closest('button')) return;
    
    // Se está no meio de um snap, ignora
    if (isSnappingRef.current) return;
    
    // Se ainda não começou a rolar, inicia a primeira transição
    if (!hasStartedScrolling) {
      setHasStartedScrolling(true);
      if (introVideoRef.current) {
        introVideoRef.current.pause();
      }
      // Vai para o FINAL da primeira transição (reproduz transicao_1)
      allowedTransitionRef.current = 0;
      setTimeout(() => scrollToEndOfTransition(0), 100);
      return;
    }
    
    // Se já está no final do último vídeo, não faz nada
    if (scrollProgress >= 0.95) return;
    
    // Usa a transição permitida atual para determinar a próxima
    const currentAllowed = allowedTransitionRef.current;
    const nextTransitionIndex = Math.min(currentAllowed + 1, videoDurations.length - 1);
    
    // Se já está na última transição, vai para o final dela
    if (currentAllowed === nextTransitionIndex) {
      scrollToEndOfTransition(currentAllowed);
    } else {
      // Avança para a próxima transição
      scrollToEndOfTransition(nextTransitionIndex);
    }
  }, [hasStartedScrolling, videoDurations.length, scrollProgress, scrollToEndOfTransition]);

  return (
    <PageContainer 
      $scrollHeight={scrollHeight || window.innerHeight * 2}
    >
      {/* Vídeo de intro em loop - aparece antes do scroll */}
      {!hasStartedScrolling && (
        <VideoContainer style={{ zIndex: 2 }}>
          <Video
            ref={introVideoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setIntroVideoLoaded(true)}
          >
            <source src="/bio/video_1.mp4" type="video/mp4" />
          </Video>
        </VideoContainer>
      )}

      {/* Vídeos de transição controlados por scroll - Sistema de camadas para evitar flash preto */}
      {/* Vídeo 1 - sempre renderizado, visível quando é o atual OU é o fallback durante transição */}
      <VideoContainer style={{ 
        zIndex: currentVideoIndex === 0 ? 3 : (previousVideoIndex === 0 ? 1 : 0),
        visibility: hasStartedScrolling ? 'visible' : 'hidden'
      }}>
        <Video
          ref={video1Ref}
          onLoadedMetadata={() => handleLoadedMetadata(0)}
          onCanPlayThrough={() => handleCanPlayThrough(0)}
          onCanPlay={() => handleCanPlayThrough(0)}
          muted
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
          style={{ 
            opacity: hasStartedScrolling && (currentVideoIndex === 0 || previousVideoIndex === 0) ? 1 : 0,
            transition: 'none',
            willChange: 'opacity'
          }}
        >
          <source src="/bio/transicao_1.mp4" type="video/mp4" />
        </Video>
      </VideoContainer>
      {/* Vídeo 2 */}
      <VideoContainer style={{ 
        zIndex: currentVideoIndex === 1 ? 3 : (previousVideoIndex === 1 ? 1 : 0),
        visibility: hasStartedScrolling ? 'visible' : 'hidden'
      }}>
        <Video
          ref={video2Ref}
          onLoadedMetadata={() => handleLoadedMetadata(1)}
          onCanPlayThrough={() => handleCanPlayThrough(1)}
          onCanPlay={() => handleCanPlayThrough(1)}
          muted
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
          style={{ 
            opacity: hasStartedScrolling && (currentVideoIndex === 1 || previousVideoIndex === 1) ? 1 : 0,
            transition: 'none',
            willChange: 'opacity'
          }}
        >
          <source src="/bio/transicao_2.mp4" type="video/mp4" />
        </Video>
      </VideoContainer>
      {/* Vídeo 3 */}
      <VideoContainer style={{ 
        zIndex: currentVideoIndex === 2 ? 3 : (previousVideoIndex === 2 ? 1 : 0),
        visibility: hasStartedScrolling ? 'visible' : 'hidden'
      }}>
        <Video
          ref={video3Ref}
          onLoadedMetadata={() => handleLoadedMetadata(2)}
          onCanPlayThrough={() => handleCanPlayThrough(2)}
          onCanPlay={() => handleCanPlayThrough(2)}
          muted
          playsInline
          preload="auto"
          webkit-playsinline="true"
          x5-playsinline="true"
          style={{ 
            opacity: hasStartedScrolling && (currentVideoIndex === 2 || previousVideoIndex === 2) ? 1 : 0,
            transition: 'none',
            willChange: 'opacity'
          }}
        >
          <source src="/bio/transicao_3.mp4" type="video/mp4" />
        </Video>
      </VideoContainer>
      
      {/* Indicador de scroll no início */}
      {!hasStartedScrolling && introVideoLoaded && (
        <ScrollHint />
      )}
      
      {/* Barra de navegação lateral */}
      <ScrollNavBar $visible={hasStartedScrolling && isLoaded}>
        <ScrollTrack
          ref={scrollTrackRef}
          onMouseDown={handleTrackMouseDown}
          onTouchStart={handleTrackTouchStart}
        >
          <ScrollProgress $progress={scrollProgress * 100} />
          {markerPositions.map((position, index) => {
            const isLastMarker = index === markerPositions.length - 1;
            const isActive = isLastMarker 
              ? scrollProgress >= 0.98 
              : currentVideoIndex === index;
            const label = isLastMarker ? 'Final' : `Transição ${index + 1}`;
            
            return (
              <Marker
                key={index}
                $position={Math.min(position, 97)}
                $active={isActive}
                onClick={() => handleMarkerClick(index)}
                aria-label={`Ir para ${label}`}
              >
                <MarkerTooltip>{label}</MarkerTooltip>
              </Marker>
            );
          })}
        </ScrollTrack>
      </ScrollNavBar>

      {/* Botão de formulário - aparece no final do transicao_3 */}
      {showFormButton && (
        <VapiButtonContainer>
          <VapiCallButton onClick={handleOpenModal}>
            <FaPhone style={{ transform: 'scaleX(-1)' }} />
          </VapiCallButton>
          <VapiButtonText>Falar com Bianca</VapiButtonText>
        </VapiButtonContainer>
      )}

      {/* Modal */}
      {isModalOpen && (
        <ModalOverlay onClick={handleOverlayClick} isClosing={isModalClosing}>
          <ModalContent isClosing={isModalClosing}>
            <ModalBackground />
            
            {/* Elementos de fundo tecnológico */}
            <TechBackground />
            <CircuitLines />
            <BorderGlow />
            <TechParticles />
            <HolographicScanner />
            
            <ModalBody>
              <ModalTitle>Receba uma Ligação</ModalTitle>
              <CloseButton onClick={handleCloseModal} />
              
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label htmlFor="nome">Nome</Label>
                  <Input 
                    type="text" 
                    id="nome" 
                    name="nome" 
                    value={formData.nome} 
                    onChange={handleChange} 
                    required 
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="telefone">Telefone</Label>
                  <Input 
                    type="tel" 
                    id="telefone" 
                    name="telefone" 
                    value={formData.telefone} 
                    onChange={handleChange} 
                    required 
                    maxLength={15}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="empresa">Nome da Empresa</Label>
                  <Input 
                    type="text" 
                    id="empresa" 
                    name="empresa" 
                    value={formData.empresa} 
                    onChange={handleChange} 
                    required 
                  />
                </FormGroup>
                
                <SubmitButton type="submit" disabled={loading}>
                  {loading ? (
                    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      PROCESSANDO <LoadingSpinner />
                    </span>
                  ) : (
                    'ENVIAR'
                  )}
                </SubmitButton>
                
                {status.show && (
                  <StatusMessage success={status.success}>
                    <StatusIcon success={status.success}>
                      {status.success ? '✓' : '✕'}
                    </StatusIcon>
                    <StatusText>
                      {status.message}
                    </StatusText>
                  </StatusMessage>
                )}
              </Form>
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default BioPage;
